/**
 * Vera Ideas route tree, composable under any parent path.
 *
 * The Vera Shell mounts it at `/ideas/*` via `ideasRoutes('/ideas')`. Ideas is
 * Postgres-backed (BFF `/api/ideas/*`) and — unlike the other modules — open
 * to EVERY authenticated employee: the ProtectedRoute has no role prefix.
 * The submission bar is meant to be as low as the access bar.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner, Toaster } from '@fluentui/react-components';
import { LightbulbRegular, SettingsRegular } from '@fluentui/react-icons';
import { AppShell, type NavItem } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import { NotificationsBell } from '@ideas/ui/NotificationsBell';
import { useIdeasMe } from '@ideas/api/useIdeas';

const BoardPage = lazy(() => import('@ideas/pages/board').then(m => ({ default: m.BoardPage })));
const IdeaDetailPage = lazy(() => import('@ideas/pages/detail').then(m => ({ default: m.IdeaDetailPage })));
const IdeasAdminPage = lazy(() => import('@ideas/pages/admin').then(m => ({ default: m.IdeasAdminPage })));

// Daybreak Amber — light header, AppShell auto-switches to charcoal foregrounds
export const IDEAS_ACCENT = '#F79E31';
export const IDEAS_TOASTER_ID = 'ideas-toaster';

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

/**
 * Mount-point prefix for every absolute `<Link>` inside the Ideas tree.
 * Empty string standalone; `/ideas` when mounted in the shell.
 */
const IdeasBasePathContext = createContext<string>('');

export function useIdeasBasePath(): string {
  return useContext(IdeasBasePathContext);
}

function buildNavItems(basePath: string, isAdmin: boolean): NavItem[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Idea Board', icon: LightbulbRegular },
    // Tech team only: rotating-prompt manager + triage summary.
    ...(isAdmin ? [{ path: p('/admin'), label: 'Ideas Admin', icon: SettingsRegular }] : []),
  ];
}

export function IdeasRootLayout() {
  const basePath = useContext(IdeasBasePathContext);
  const me = useIdeasMe();
  const navItems = buildNavItems(basePath, me.data?.isAdmin ?? false);
  return (
    <ProtectedRoute>
      <ErrorBoundary>
        <AppShell
          appId="ideas"
          appTitle="Vera Ideas"
          headerColor={IDEAS_ACCENT}
          navItems={navItems}
          headerExtra={<NotificationsBell />}
        >
          <Toaster toasterId={IDEAS_TOASTER_ID} />
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Ideas route tree, with sidebar links prefixed for the mount
 * path. Standalone: `ideasRoutes()` at repo root. Shell: `ideasRoutes('/ideas')`.
 */
export function ideasRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <IdeasBasePathContext.Provider value={basePath}>
          <IdeasRootLayout />
        </IdeasBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><BoardPage /></LazyPage> },
        { path: 'admin', element: <LazyPage><IdeasAdminPage /></LazyPage> },
        { path: ':id', element: <LazyPage><IdeaDetailPage /></LazyPage> },
        { path: '*', element: <Navigate to={basePath || '/'} replace /> },
      ],
    },
  ];
}
